import React, { useState, useEffect } from 'react';
import {
  CalendarIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import { Bar, Pie } from 'react-chartjs-2';

import { nutritionApi } from '../api/nutrition';
import { useFormatting } from '../hooks/useFormatting';

interface WeeklyReportProps {
  selectedLocation?: string;
  onReportGenerated: () => void;
}

interface WeeklyReportData {
  week: { start: string; end: string };
  location?: string;
  summary: {
    totalMixes: number;
    totalFeedUsed: number;
    totalCost: number;
    averageCostPerKg: number;
    averageQualityScore: number;
  };
  nutritionalAverages: {
    protein: number;
    fat: number;
    fiber: number;
    moisture: number;
    dailyQuantity: number;
    dailyCalories: number;
  };
  dailyBreakdown: Array<{
    date: string;
    protein: number;
    fat: number;
    fiber: number;
    quality: number;
    quantity: number;
    cost: number;
    approved: boolean;
  }>;
  feedComponents: Array<{
    feedStockId: string;
    name: string;
    totalQuantity: number;
    percentage: number;
  }>;
  recommendations: string[];
}

export default function WeeklyReport({ selectedLocation, onReportGenerated }: WeeklyReportProps) {
  const { formatCurrency, formatDate } = useFormatting();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<WeeklyReportData | null>(null);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const today = new Date();
    return format(startOfWeek(today), 'yyyy-MM-dd');
  });
  const [isGenerating, setIsGenerating] = useState(false);

  const loadWeeklyReport = async (weekStart: string, location?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await nutritionApi.getWeeklyReport(weekStart, location);
      setReportData(data);
      onReportGenerated();
    } catch (err: any) {
      setError(err.message || 'Failed to load weekly report');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWeeklyReport(selectedWeek, selectedLocation);
  }, [selectedWeek, selectedLocation]);

  const navigateWeek = (direction: 'prev' | 'next') => {
    const currentWeek = new Date(selectedWeek);
    const newWeek = direction === 'prev' ? subWeeks(currentWeek, 1) : addWeeks(currentWeek, 1);
    setSelectedWeek(format(newWeek, 'yyyy-MM-dd'));
  };

  const generatePDFReport = async () => {
    if (!reportData) return;

    setIsGenerating(true);
    try {
      // In a real implementation, you would call an API endpoint to generate PDF
      // For now, we'll simulate the action
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create a simple text-based report for download
      const reportContent = `
WEEKLY NUTRITIONAL REPORT
Week: ${formatDate(new Date(reportData.week.start))} - ${formatDate(new Date(reportData.week.end))}
Location: ${reportData.location || 'All Locations'}

SUMMARY
=======
Total Feed Mixes: ${reportData.summary.totalMixes}
Total Feed Used: ${reportData.summary.totalFeedUsed.toFixed(1)} kg
Total Cost: ${formatCurrency(reportData.summary.totalCost)}
Average Cost per kg: ${formatCurrency(reportData.summary.averageCostPerKg)}
Average Quality Score: ${reportData.summary.averageQualityScore.toFixed(1)}/100

NUTRITIONAL AVERAGES
==================
Protein: ${reportData.nutritionalAverages.protein.toFixed(1)}%
Fat: ${reportData.nutritionalAverages.fat.toFixed(1)}%
Fiber: ${reportData.nutritionalAverages.fiber.toFixed(1)}%
Moisture: ${reportData.nutritionalAverages.moisture.toFixed(1)}%
Daily Quantity: ${reportData.nutritionalAverages.dailyQuantity.toFixed(1)} kg
Daily Calories: ${reportData.nutritionalAverages.dailyCalories.toFixed(0)} kcal

DAILY BREAKDOWN
==============
${reportData.dailyBreakdown.map(day => 
  `${formatDate(new Date(day.date))}: ${day.quantity.toFixed(1)}kg, Quality: ${day.quality}/100, Cost: ${formatCurrency(day.cost)} ${day.approved ? '✓' : '⚠'}`
).join('\n')}

FEED COMPONENTS
==============
${reportData.feedComponents.map(comp => 
  `${comp.name}: ${comp.totalQuantity.toFixed(1)}kg (${comp.percentage.toFixed(1)}%)`
).join('\n')}

RECOMMENDATIONS
==============
${reportData.recommendations.map(rec => `• ${rec}`).join('\n')}
      `.trim();

      // Create and download the file
      const blob = new Blob([reportContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly-report-${reportData.week.start}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to generate PDF report');
    } finally {
      setIsGenerating(false);
    }
  };

  // Chart configurations
  const nutritionalTrendsData = reportData && reportData.dailyBreakdown ? {
    labels: (reportData.dailyBreakdown || []).map(day => formatDate(new Date(day.date))),
    datasets: [
      {
        label: 'Protein %',
        data: (reportData.dailyBreakdown || []).map(day => day.protein || 0),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
      },
      {
        label: 'Fat %',
        data: (reportData.dailyBreakdown || []).map(day => day.fat || 0),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
      },
      {
        label: 'Fiber %',
        data: (reportData.dailyBreakdown || []).map(day => day.fiber || 0),
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
      },
    ],
  } : null;

  const feedComponentsData = reportData && reportData.feedComponents ? {
    labels: (reportData.feedComponents || []).map(comp => comp.name || 'Unknown'),
    datasets: [
      {
        data: (reportData.feedComponents || []).map(comp => comp.percentage || 0),
        backgroundColor: [
          '#3B82F6',
          '#10B981',
          '#F59E0B',
          '#EF4444',
          '#8B5CF6',
          '#F97316',
          '#06B6D4',
          '#84CC16'
        ].slice(0, (reportData.feedComponents || []).length),
      },
    ],
  } : null;

  if (isLoading && !reportData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
        <span className="ml-2 text-slate-600">Loading weekly report...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Navigation */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-slate-900">Weekly Report</h3>
            <p className="mt-1 text-sm text-slate-600">
              Comprehensive nutritional analysis and feed usage summary
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateWeek('prev')}
                className="p-2 text-slate-400 hover:text-slate-600"
                title="Previous week"
              >
                ←
              </button>
              <div className="text-center">
                <input
                  type="date"
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className="block input border-gray-300 shadow-sm sm:text-sm"
                />
                {reportData && (
                  <div className="text-xs text-slate-500 mt-1">
                    {formatDate(new Date(reportData.week.start))} - {formatDate(new Date(reportData.week.end))}
                  </div>
                )}
              </div>
              <button
                onClick={() => navigateWeek('next')}
                className="p-2 text-slate-400 hover:text-slate-600"
                title="Next week"
              >
                →
              </button>
            </div>
            <button
              onClick={generatePDFReport}
              disabled={!reportData || isGenerating}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Export Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {reportData && reportData.summary && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="text-2xl font-bold text-cyan-600">{reportData.summary?.totalMixes || 0}</div>
              <div className="text-sm text-slate-500">Total Mixes</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-2xl font-bold text-green-600">{(Number(reportData.summary?.totalFeedUsed) || 0).toFixed(1)}</div>
              <div className="text-sm text-slate-500">kg Feed Used</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-2xl font-bold text-cyan-600">{formatCurrency(reportData.summary?.totalCost || 0)}</div>
              <div className="text-sm text-slate-500">Total Cost</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-2xl font-bold text-purple-600">{formatCurrency(reportData.summary?.averageCostPerKg || 0)}</div>
              <div className="text-sm text-slate-500">Cost per kg</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6 text-center">
                <div className="text-2xl font-bold text-orange-600">{(Number(reportData.summary?.averageQualityScore) || 0).toFixed(1)}</div>
              <div className="text-sm text-slate-500">Avg Quality</div>
            </div>
          </div>

          {/* Nutritional Averages */}
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-medium text-slate-900 mb-4">Nutritional Averages</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              <div className="text-center">
                <div className="text-xl font-semibold text-cyan-600">{(Number(reportData.nutritionalAverages?.protein) || 0).toFixed(1)}%</div>
                <div className="text-sm text-slate-500">Protein</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-green-600">{(Number(reportData.nutritionalAverages?.fat) || 0).toFixed(1)}%</div>
                <div className="text-sm text-slate-500">Fat</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-yellow-600">{(Number(reportData.nutritionalAverages?.fiber) || 0).toFixed(1)}%</div>
                <div className="text-sm text-slate-500">Fiber</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-cyan-600">{(Number(reportData.nutritionalAverages?.moisture) || 0).toFixed(1)}%</div>
                <div className="text-sm text-slate-500">Moisture</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-purple-600">{(Number(reportData.nutritionalAverages?.dailyQuantity) || 0).toFixed(1)}</div>
                <div className="text-sm text-slate-500">kg/day</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-orange-600">{(Number(reportData.nutritionalAverages?.dailyCalories) || 0).toFixed(0)}</div>
                <div className="text-sm text-slate-500">kcal/day</div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Nutritional Trends */}
            <div className="bg-white rounded-lg shadow p-6">
              <h4 className="text-lg font-medium text-slate-900 mb-4">Daily Nutritional Trends</h4>
              {nutritionalTrendsData && (
                <div className="h-64">
                  <Bar
                    data={nutritionalTrendsData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top' as const,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Percentage (%)'
                          }
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>

            {/* Feed Components Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <h4 className="text-lg font-medium text-slate-900 mb-4">Feed Components Distribution</h4>
              {feedComponentsData && (
                <div className="h-64">
                  <Pie
                    data={feedComponentsData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'right' as const,
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Daily Breakdown Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-lg font-medium text-slate-900">Daily Breakdown</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Protein</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Fat</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Fiber</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quality</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(reportData.dailyBreakdown || []).map((day, index) => (
                    <tr key={index} className="hover:bg-slate-50/80">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {formatDate(new Date(day.date))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {(Number(day.quantity) || 0).toFixed(1)} kg
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {(Number(day.protein) || 0).toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {(Number(day.fat) || 0).toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {(Number(day.fiber) || 0).toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (Number(day.quality) || 0) >= 80 ? 'bg-emerald-100 text-emerald-800' :
                          (Number(day.quality) || 0) >= 60 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
                        }`}>
                          {Number(day.quality) || 0}/100
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {formatCurrency(Number(day.cost) || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {day.approved ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Feed Components Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-medium text-slate-900 mb-4">Feed Components Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(reportData.feedComponents || []).map((component, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium text-slate-900">{component.name || 'Unknown Component'}</h5>
                    <span className="text-sm text-slate-500">{(Number(component.percentage) || 0).toFixed(1)}%</span>
                  </div>
                  <div className="mt-2">
                    <div className="text-sm text-slate-600">
                      Total: {(Number(component.totalQuantity) || 0).toFixed(1)} kg
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-cyan-600 h-2 rounded-full"
                        style={{ width: `${Math.min(Number(component.percentage) || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {(reportData.recommendations || []).length > 0 && (
            <div className="bg-cyan-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <BeakerIcon className="h-5 w-5 text-cyan-600 mr-2" />
                <h4 className="font-medium text-blue-900">Weekly Recommendations</h4>
              </div>
              <ul className="space-y-2">
                {reportData.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 h-1.5 w-1.5 rounded-full bg-blue-400 mt-2 mr-3"></div>
                    <span className="text-sm text-cyan-800">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {!reportData && !isLoading && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CalendarIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-medium text-slate-900">No Report Data</h3>
          <p className="mt-1 text-sm text-slate-500">
            No feed mixes found for the selected week.
          </p>
        </div>
      )}

      {!reportData && !isLoading && !error && (
        <div className="bg-slate-50/80 border border-gray-200 rounded-lg p-6 text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Report Data</h3>
          <p className="text-slate-600">Select a week to generate a report or check if data is available for the selected period.</p>
        </div>
      )}
    </div>
  );
} 